/**
 * /gap-reports — full-fidelity archive of every strategic gap analysis
 * ever generated on /competitive.
 *
 * Left column: chronological list, newest first, with headline + gap count.
 * Right column: full render of the selected report (gaps + strengths).
 * Deep-links via ?id=<uuid> so a report can be shared / bookmarked.
 */
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight, History, Sparkles, TrendingUp } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SectionHeader } from '@/components/charts/SectionHeader';
import { cn } from '@/lib/utils';
import {
  useCompetitiveGapReport,
  useCompetitiveGapReports,
  type GapReportRow,
} from '@/hooks/useCompetitiveGapReports';

interface Gap {
  dimension: string;
  title: string;
  flaxValue: string;
  cohortValue: string;
  delta: string;
  severity: 'high' | 'medium' | 'low';
  evidence: string;
  monthlyAction: string;
  expectedImpact: string;
}

interface Strength {
  title: string;
  evidence: string;
}

export function GapReportsPage() {
  const list = useCompetitiveGapReports(100);
  const [params, setParams] = useSearchParams();
  const paramId = params.get('id');

  const selectedId = useMemo(() => {
    if (paramId) return paramId;
    return list.data?.[0]?.id ?? null;
  }, [paramId, list.data]);

  const report = useCompetitiveGapReport(selectedId);

  const setSelected = (id: string) => {
    const next = new URLSearchParams(params);
    next.set('id', id);
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <SectionHeader
        title={
          <span className="flex items-center gap-2">
            <History className="h-5 w-5 text-violet-500" />
            Strategic gap reports
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-violet-500/10 px-2 py-0.5 text-2xs font-medium text-violet-700 dark:text-violet-300">
              <Sparkles className="h-3 w-3" /> archive
            </span>
          </span>
        }
        description="Every generation from /competitive, saved with timestamp. Click any run to see the full snapshot."
      />

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <aside className="space-y-2">
          {list.isLoading && (
            <div className="rounded-md border border-border/40 bg-muted/20 p-4 text-2xs text-muted-foreground">
              Loading…
            </div>
          )}
          {list.data && list.data.length === 0 && (
            <div className="rounded-md border border-dashed border-border/40 bg-muted/20 p-4 text-2xs text-muted-foreground">
              No reports yet. Click "New report" on /competitive to generate the first one.
            </div>
          )}
          {list.data && list.data.length > 0 && (
            <ol className="space-y-2">
              {list.data.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => setSelected(r.id)}
                    className={cn(
                      'w-full rounded-md border p-3 text-left transition',
                      selectedId === r.id
                        ? 'border-violet-500/40 bg-violet-500/[0.06]'
                        : 'border-border/40 bg-card hover:bg-accent',
                    )}
                  >
                    <div className="flex items-center gap-2 text-2xs text-muted-foreground">
                      <span className="tabular-nums">
                        {new Date(r.generated_at).toLocaleString('en-IN', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      <span className="ml-auto rounded-full bg-muted/60 px-1.5 py-0.5 tabular-nums">
                        {r.gap_count ?? 0} gaps
                      </span>
                    </div>
                    <div className="mt-1 line-clamp-2 text-xs font-medium leading-snug">
                      {r.headline || '—'}
                    </div>
                    <div className="mt-1 text-[10px] text-muted-foreground tabular-nums">
                      window {r.window_days}d · {r.strength_count ?? 0} strengths
                    </div>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </aside>

        <section className="min-w-0 space-y-4">
          {report.isLoading && !report.data && (
            <div className="rounded-md border border-border/40 bg-muted/20 p-8 text-center text-sm text-muted-foreground">
              Loading report…
            </div>
          )}
          {report.data && <ReportDetail report={report.data} listRow={list.data?.find((r) => r.id === report.data!.id)} />}
          {!report.isLoading && !report.data && !selectedId && (
            <div className="rounded-md border border-dashed border-border/40 bg-muted/20 p-8 text-center text-sm text-muted-foreground">
              Pick a report from the list.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function ReportDetail({
  report,
  listRow,
}: {
  report: {
    id: string;
    generated_at: string;
    window_days: number;
    headline: string | null;
    gap_count: number | null;
    strength_count: number | null;
    response_json: {
      headline?: string;
      gaps?: unknown[];
      strengths?: unknown[];
      fallback?: string;
      partial?: boolean;
      [k: string]: unknown;
    };
  };
  listRow?: GapReportRow;
}) {
  const gaps = (report.response_json.gaps ?? []) as Gap[];
  const strengths = (report.response_json.strengths ?? []) as Strength[];
  const headline = report.response_json.headline || report.headline || 'Untitled report';

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border/40 bg-gradient-to-br from-emerald-500/5 to-sky-500/5 p-4">
        <div className="flex items-center gap-2 text-2xs text-muted-foreground">
          <History className="h-3 w-3 text-violet-500" />
          Archived report ·{' '}
          {new Date(report.generated_at).toLocaleString('en-IN', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
          · window {report.window_days}d · {listRow?.gap_count ?? gaps.length} gaps · {listRow?.strength_count ?? strengths.length} strengths
        </div>
        <h3 className="mt-1 text-lg font-semibold leading-tight">{headline}</h3>
      </div>

      {gaps.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Gaps to close</h4>
          <div className="grid gap-3 lg:grid-cols-2">
            {gaps.map((g, i) => (
              <GapCard key={i} gap={g} />
            ))}
          </div>
        </div>
      )}

      {strengths.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Strengths to defend</h4>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {strengths.map((s, i) => (
              <StrengthCard key={i} strength={s} />
            ))}
          </div>
        </div>
      )}

      {report.response_json.fallback && (
        <div className="whitespace-pre-wrap rounded-md border border-border/40 bg-card p-4 text-sm">
          {report.response_json.fallback as string}
        </div>
      )}
    </div>
  );
}

function GapCard({ gap }: { gap: Gap }) {
  const severityColor =
    gap.severity === 'high'
      ? 'bg-rose-500/15 text-rose-700 dark:text-rose-400'
      : gap.severity === 'medium'
        ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
        : 'bg-slate-500/15 text-slate-700 dark:text-slate-400';

  return (
    <div className="rounded-md border border-border/40 bg-card p-4">
      <div className="flex items-start gap-2">
        <ArrowDownRight className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="muted" className="text-2xs">
              {gap.dimension}
            </Badge>
            <span className={cn('rounded px-1.5 py-0.5 text-2xs font-medium', severityColor)}>
              {gap.severity}
            </span>
          </div>
          <h5 className="mt-1.5 text-sm font-semibold leading-tight">{gap.title}</h5>
          <p className="mt-1 text-xs text-muted-foreground">{gap.evidence}</p>
        </div>
      </div>

      <dl className="mt-3 grid grid-cols-1 gap-x-3 gap-y-1.5 text-2xs">
        <Row label="Flax" value={gap.flaxValue} />
        <Row label="Cohort" value={gap.cohortValue} />
        <Row label="Delta" value={gap.delta} emphasize />
      </dl>

      <div className="mt-3 rounded-md border border-emerald-500/20 bg-emerald-500/5 p-3">
        <div className="flex items-center gap-1.5 text-2xs font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
          <TrendingUp className="h-3 w-3" />
          This month
        </div>
        <p className="mt-1 text-xs leading-relaxed">{gap.monthlyAction}</p>
        <p className="mt-1.5 text-2xs text-muted-foreground">{gap.expectedImpact}</p>
      </div>
    </div>
  );
}

function StrengthCard({ strength }: { strength: Strength }) {
  return (
    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
      <div className="flex items-start gap-2">
        <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
        <div className="min-w-0">
          <h5 className="text-sm font-semibold leading-tight">{strength.title}</h5>
          <p className="mt-1 text-xs text-muted-foreground">{strength.evidence}</p>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, emphasize }: { label: string; value: string; emphasize?: boolean }) {
  return (
    <div className="flex gap-2">
      <dt className="w-14 shrink-0 text-muted-foreground">{label}</dt>
      <dd className={cn('text-foreground/90', emphasize && 'font-medium')}>{value}</dd>
    </div>
  );
}
